import React, { useState, useEffect, useCallback } from "react";
import { Globe, Loader2, Trash2, Sparkles, KeyRound, Check, ExternalLink, Link2, Pin, Coins } from "lucide-react";
import { scrapeSettingsGet, scrapeSettingsSave, scrapeRun, scrapeList, scrapeDelete, scrapeSourceAdd, scrapeSourceList, scrapeSourceDelete } from "../lib/api";
import { toast } from "sonner";

const KeySetup = ({ userId, cfg, onSaved }) => {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!key.trim()) return;
    setBusy(true);
    try { const r = await scrapeSettingsSave(userId, key.trim()); setKey(""); onSaved(r); toast.success("ScrapeGraphAI key saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save key"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-[#060b18] border border-[#00F0FF22] rounded-xl p-3 space-y-2" data-testid="scrape-key-setup">
      <div className="flex items-center gap-2 flex-wrap">
        <KeyRound className="w-4 h-4 text-cyan-300/60" />
        {cfg?.configured
          ? <span className="text-cyan-100 text-sm flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-300" /> Connected <span className="text-cyan-300/40 font-mono text-xs">{cfg.masked}</span>
              {cfg.credits_remaining != null && <span className="text-cyan-300/50 text-xs flex items-center gap-1 ml-2"><Coins className="w-3 h-3" /> {cfg.credits_remaining} credits</span>}</span>
          : <span className="text-amber-200/80 text-xs">Add your ScrapeGraphAI key (sgai-…) to turn on scraping.</span>}
        <a href="https://scrapegraphai.com" target="_blank" rel="noreferrer" className="ml-auto text-[#00F0FF] text-[11px] flex items-center gap-1 hover:underline">get a key <ExternalLink className="w-3 h-3" /></a>
      </div>
      <div className="flex items-center gap-2">
        <input value={key} onChange={(e) => setKey(e.target.value)} type="password" placeholder={cfg?.configured ? "replace key" : "paste sgai-…"} data-testid="scrape-key-input"
          className="flex-1 bg-[#0a1020] border border-[#00F0FF22] rounded-lg px-3 py-1.5 text-cyan-50 text-sm" />
        <button onClick={save} disabled={busy} data-testid="scrape-key-save" className="px-3 py-1.5 rounded-lg text-xs font-bold text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
};

const DataView = ({ data }) => {
  if (data == null) return null;
  if (typeof data === "object" && data.content && Object.keys(data).length === 1) {
    return <p className="text-cyan-100/85 text-sm whitespace-pre-wrap leading-relaxed">{String(data.content).slice(0, 4000)}</p>;
  }
  return <pre className="text-cyan-100/85 text-xs whitespace-pre-wrap leading-relaxed overflow-x-auto bg-[#0a1020] rounded-lg p-3 border border-[#00F0FF14]">{JSON.stringify(data, null, 2).slice(0, 6000)}</pre>;
};

export default function NexusScrape({ session }) {
  const userId = session.user_id;
  const [cfg, setCfg] = useState(null);
  const [url, setUrl] = useState("");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [sources, setSources] = useState([]);
  const [showSources, setShowSources] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, l, s] = await Promise.all([scrapeSettingsGet(userId), scrapeList(userId), scrapeSourceList(userId)]);
      setCfg(c); setItems(l.items || []); setSources(s.items || []);
    } catch (e) {}
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    if (!url.trim()) { toast.error("Paste a URL first"); return; }
    setBusy(true);
    try {
      const r = await scrapeRun(userId, url.trim(), prompt.trim());
      setItems((h) => [r, ...h]); setUrl(""); setPrompt("");
      toast.success("Scraped");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't scrape that page"); }
    finally { setBusy(false); }
  };

  const del = async (id) => { try { await scrapeDelete(userId, id); setItems((h) => h.filter((x) => x.id !== id)); } catch (e) {} };

  const pinToBriefs = async (rec) => {
    try {
      const r = await scrapeSourceAdd(userId, rec.url, rec.prompt, "both");
      setSources((h) => [r, ...h]);
      toast.success("Pinned — Nexus will read this in your trade & crypto briefs");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't pin"); }
  };

  const delSource = async (id) => { try { await scrapeSourceDelete(userId, id); setSources((h) => h.filter((x) => x.id !== id)); } catch (e) {} };

  return (
    <div className="h-full overflow-y-auto bg-[#030712] p-4 sm:p-6" data-testid="scrape-panel">
      <div className="max-w-3xl mx-auto space-y-5">
        <div>
          <h2 className="text-[#00F0FF] font-bold text-2xl flex items-center gap-2"><Globe className="w-6 h-6" /> Web Scrape</h2>
          <p className="text-cyan-200/60 text-sm mt-1">Drop any link + tell me your wish — I'll read the live page (bypassing JS/anti-bot walls) and pull exactly what you want. You can also just paste a link in <b>chat</b>. Pin a page to feed it into your trade & crypto briefs.</p>
        </div>

        <KeySetup userId={userId} cfg={cfg} onSaved={(r) => setCfg(r)} />

        <div className="bg-[#060b18] border border-[#00F0FF33] rounded-2xl p-3 space-y-3">
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-cyan-300/60 shrink-0" />
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://… any page, article, dashboard or profile" data-testid="scrape-url-input"
              className="flex-1 bg-[#0a1020] border border-[#00F0FF22] rounded-lg px-3 py-2 text-cyan-50 text-sm outline-none" />
          </div>
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2}
            placeholder="Your wish — what should I pull? (e.g. 'this trader's open positions and win rate', or leave blank to grab everything)"
            data-testid="scrape-prompt-input"
            className="w-full bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2 text-cyan-50 text-sm outline-none resize-none" />
          <button onClick={run} disabled={busy} data-testid="scrape-run-btn"
            className="w-full sm:w-auto px-5 py-2.5 rounded-xl text-sm font-bold text-[#030712] bg-[#00F0FF] hover:bg-cyan-300 disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Scraping…</> : <><Sparkles className="w-4 h-4" /> Scrape page</>}
          </button>
        </div>

        <div className="bg-[#060b18] border border-[#00F0FF14] rounded-xl p-3">
          <button onClick={() => setShowSources((v) => !v)} data-testid="scrape-sources-toggle" className="w-full flex items-center gap-2 text-cyan-200/80 text-sm font-semibold">
            <Pin className="w-4 h-4 text-cyan-300/60" /> Pages pinned to your briefs ({sources.length}) <span className="ml-auto text-cyan-300/40 text-xs">{showSources ? "hide" : "show"}</span>
          </button>
          {showSources && (
            <div className="mt-2 space-y-1.5">
              {sources.length === 0 ? <p className="text-cyan-300/40 text-xs">None yet. Scrape a page below, then tap “Pin to briefs”.</p>
                : sources.map((s) => (
                  <div key={s.id} className="flex items-center gap-2 text-xs" data-testid="scrape-source-item">
                    <span className="text-cyan-100/80 truncate flex-1">{s.url}</span>
                    <span className="text-cyan-300/40 uppercase">{s.kind}</span>
                    <button onClick={() => delSource(s.id)} className="text-cyan-200/40 hover:text-rose-300"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
            </div>
          )}
        </div>

        {items.length > 0 ? (
          <div className="space-y-3">
            {items.map((r) => (
              <div key={r.id} className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl p-4 space-y-2" data-testid="scrape-result-item">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <a href={r.url} target="_blank" rel="noreferrer" className="text-[#00F0FF] text-sm font-semibold hover:underline truncate block">{r.url}</a>
                    {r.prompt && <p className="text-cyan-300/50 text-xs mt-0.5">“{r.prompt}”</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => pinToBriefs(r)} data-testid="scrape-pin-btn" title="Feed into trade & crypto briefs" className="text-cyan-200/50 hover:text-[#00F0FF]"><Pin className="w-4 h-4" /></button>
                    <button onClick={() => del(r.id)} data-testid="scrape-delete" className="text-cyan-200/40 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <DataView data={r.data} />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-cyan-200/40" data-testid="scrape-empty">
            <Globe className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No scrapes yet. Paste a link above with your wish, or just drop a link in chat.</p>
          </div>
        )}
      </div>
    </div>
  );
}
